import { ApiConfig } from '../../shared/apiConfig'
import { authenticatedApiInstance, publicApiInstance } from '../client'

export type AuthResponse = {
  accessToken: string
  refreshToken: string
  authCode: string
  expiresIn: number
}

export type MessageResponse = {
  message: string
}

export enum OAuthProvider {
  Google = 'Google',
  Apple = 'Apple'
}

export const credentialsSignIn = async (
  bodyParams: {
    email: string
    password: string
  },
  config: ApiConfig
): Promise<AuthResponse> =>
  publicApiInstance(config.tenant)
    .post(`/auth/sign-in`, bodyParams)
    .then((res) => res.data)

export const signUp = async (
  bodyParams: {
    email: string
    password: string
    firstName: string
    lastName: string
    displayFullName?: boolean
  },
  config: ApiConfig
): Promise<MessageResponse> =>
  publicApiInstance(config.tenant)
    .post(`/auth/sign-up`, bodyParams)
    .then((res) => res.data)

export const confirmSignUp = async (
  bodyParams: {
    email: string
    confirmationCode: string
  },
  config: ApiConfig
): Promise<MessageResponse> =>
  publicApiInstance(config.tenant)
    .post(`/auth/confirm-sign-up`, bodyParams)
    .then((res) => res.data)

export const resendConfirmationCode = async (
  bodyParams: {
    email: string
  },
  config: ApiConfig
): Promise<MessageResponse> =>
  publicApiInstance(config.tenant)
    .post(`/auth/resend-confirmation-code`, bodyParams)
    .then((res) => res.data)

export const forgotPassword = async (bodyParams: { email: string }, config: ApiConfig): Promise<MessageResponse> =>
  publicApiInstance(config.tenant)
    .post(`/auth/forgot-password`, bodyParams)
    .then((res) => res.data)

export const confirmForgotPassword = async (
  bodyParams: {
    email: string
    confirmationCode: string
    newPassword: string
  },
  config: ApiConfig
): Promise<MessageResponse> =>
  publicApiInstance(config.tenant)
    .post(`/auth/confirm-forgot-password`, bodyParams)
    .then((res) => res.data)

export const refreshToken = async (
  bodyParams: {
    accessJWT: string
    refreshJWT?: string
  },
  config: ApiConfig
): Promise<AuthResponse> =>
  publicApiInstance(config.tenant)
    .post(`/auth/refresh-token`, { accessToken: bodyParams.accessJWT, refreshToken: bodyParams.refreshJWT })
    .then((res) => res.data)

export const signOut = async (config: ApiConfig): Promise<MessageResponse> =>
  authenticatedApiInstance(config.accessToken, config.tenant)
    .post(`/auth/sign-out`)
    .then((res) => res.data)

export const deleteAccount = async (config: ApiConfig): Promise<MessageResponse> =>
  authenticatedApiInstance(config.accessToken, config.tenant)
    .post(`/auth/delete-account`)
    .then((res) => res.data)

export const getSocialAuthUrl = async (
  pathParams: {
    provider: OAuthProvider
  },
  queryParams: {
    redirectUri: string
    state: string
  },
  config: ApiConfig
): Promise<string> =>
  publicApiInstance(config.tenant)
    .get(`/auth/federated/${pathParams.provider}`, { params: queryParams })
    .then((res) => res.data)

export const federatedSignIn = async (
  queryParams: {
    authCode: string
    redirectUri: string
  },
  config: ApiConfig
): Promise<AuthResponse> =>
  publicApiInstance(config.tenant)
    .post(`/auth/federated/sign-in`, {}, { params: queryParams })
    .then((res) => res.data)

export const federatedSignUp = async (
  bodyParams: {
    firstName?: string
    lastName?: string
    displayFullName?: boolean
  },
  queryParams: {
    authCode: string
    redirectUri: string
  },
  config: ApiConfig
): Promise<AuthResponse> =>
  publicApiInstance(config.tenant)
    .post(`/auth/federated/sign-up`, bodyParams, { params: queryParams })
    .then((res) => res.data)

export const linkFederatedIdentity = async (
  pathParams: {
    provider: OAuthProvider
  },
  bodyParams: {
    email: string
  },
  config: ApiConfig
): Promise<MessageResponse> =>
  publicApiInstance(config.tenant)
    .post(`/auth/federated/${pathParams.provider}/link`, bodyParams)
    .then((res) => res.data)

export const authCodeSignIn = async (
  bodyParams: {
    authCode: string
  },
  config: ApiConfig
): Promise<AuthResponse> =>
  publicApiInstance(config.tenant)
    .post(`/auth/auth-code/sign-in`, bodyParams)
    .then((res) => res.data)

export const disableAccount = async (
  queryParams: {
    reason?: 'TERMS_OF_SERVICE_CONSENT' | 'VACATION_MODE'
    allowAccess?: boolean
    reactivationDate?: Date
  },
  config: ApiConfig
): Promise<MessageResponse> =>
  authenticatedApiInstance(config.accessToken, config.tenant)
    .post(
      `/auth/disable-account`,
      {},
      { params: { ...queryParams, reactivationDate: queryParams.reactivationDate?.toISOString() } }
    )
    .then((res) => res.data)

export const changePassword = async (
  bodyParams: {
    oldPassword: string
    newPassword: string
  },
  config: ApiConfig
): Promise<MessageResponse> =>
  authenticatedApiInstance(config.accessToken, config.tenant)
    .post(`/auth/change-password`, bodyParams)
    .then((res) => res.data)
